import type { SourceCookies } from '@/sources/types';

/**
 * Parse a raw `Cookie:` header value or a JSON dict into a {@link SourceCookies}
 * map. Accepts:
 *
 *   - `name1=value1; name2=value2; ...`
 *   - `Cookie: name1=value1; name2=value2; ...`
 *   - `{"name1": "value1", ...}`
 *
 * Whitespace is trimmed; empty / malformed pairs are dropped.
 */
function cookieHeaderValue(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return '';

  const lines = trimmed
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const cookieLine = lines.find((line) => /^cookie\s*:/i.test(line));
  if (cookieLine) return cookieLine.replace(/^cookie\s*:\s*/i, '').trim();

  if (lines.length <= 1) return trimmed.replace(/^cookie\s*:\s*/i, '').trim();

  // Users sometimes paste wrapped cookie pairs. Join only when every line
  // looks like a cookie pair; otherwise keep the first line and ignore
  // adjacent request headers such as `User-Agent:` / `Referer:`.
  if (lines.every((line) => /^[^:=;\s][^=;\s]*=/.test(line))) {
    return lines.join('; ');
  }
  return lines[0]?.replace(/^cookie\s*:\s*/i, '').trim() ?? '';
}

function hasInvalidHeaderChars(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= 31 || code === 127) return true;
  }
  return false;
}

export function parseCookies(input: string): SourceCookies {
  const trimmed = input.trim();
  if (!trimmed) return {};

  if (trimmed.startsWith('{')) {
    try {
      const obj = JSON.parse(trimmed) as Record<string, unknown>;
      const out: SourceCookies = {};
      for (const [k, v] of Object.entries(obj)) {
        const value = typeof v === 'string' ? v : v != null ? String(v) : undefined;
        if (value === undefined) continue;
        if (hasInvalidHeaderChars(k) || hasInvalidHeaderChars(value)) continue;
        out[k] = value;
      }
      return out;
    } catch {
      return {};
    }
  }

  const headerValue = cookieHeaderValue(trimmed);
  const out: SourceCookies = {};
  for (const segment of headerValue.split(/;\s*/)) {
    if (!segment) continue;
    const eq = segment.indexOf('=');
    if (eq <= 0) continue;
    const name = segment.slice(0, eq).trim();
    const value = segment.slice(eq + 1).trim();
    if (!name) continue;
    if (hasInvalidHeaderChars(name) || hasInvalidHeaderChars(value)) continue;
    out[name] = value;
  }
  return out;
}

/** Serialize a cookie map back to a `name1=value1; name2=value2` string. */
export function serializeCookies(cookies: SourceCookies | undefined): string {
  if (!cookies) return '';
  const parts: string[] = [];
  for (const [name, value] of Object.entries(cookies)) {
    if (!name) continue;
    parts.push(`${name}=${value}`);
  }
  return parts.join('; ');
}

/** Pick a subset of cookies by name. Missing names are silently skipped. */
export function pickCookies(cookies: SourceCookies | undefined, names: string[]): SourceCookies {
  if (!cookies) return {};
  const out: SourceCookies = {};
  for (const name of names) {
    const v = cookies[name];
    if (v !== undefined) out[name] = v;
  }
  return out;
}

/** Merge two cookie maps; `b` overrides `a` on key collision. */
export function mergeCookies(a: SourceCookies | undefined, b: SourceCookies | undefined): SourceCookies {
  return { ...(a ?? {}), ...(b ?? {}) };
}
